"use client";

import { useEffect, useRef, useState } from "react";
import Image from "next/image";
import dynamic from "next/dynamic";
import parse from "html-react-parser";
import ReactMarkdown from "react-markdown";
import { IPageContent } from "@/types/pageContent.types";
import { AnimatePresence, motion, useScroll, useTransform } from "framer-motion";

import styles from "./styles.module.scss";
import TransitionLink from "@/components/TransitionLink";
import { initStickyScroll } from "@/lib/stickyScroll";

const MotionImage = motion.create(Image);


export type IonCmsViewProps = {
  ionCmsSection1Data: IPageContent["ionCmsPageModel"]["ionCmsPage"]["ionCmsSection1"];
  ionCmsSection2Data: IPageContent["ionCmsPageModel"]["ionCmsPage"]["ionCmsSection2"];
  // ionCmsSection3Data: IPageContent["ionCmsPageModel"]["ionCmsPage"]["ionCmsSection3"];
  // ionCmsSection4Data: IPageContent["ionCmsPageModel"]["ionCmsPage"]["ionCmsSection4"];
  // ionCmsSection5Data: IPageContent["ionCmsPageModel"]["ionCmsPage"]["ionCmsSection5"];
  // ionCmsSection6Data: IPageContent["ionCmsPageModel"]["ionCmsPage"]["ionCmsSection6"];
  // ionCmsSection7Data: IPageContent["ionCmsPageModel"]["ionCmsPage"]["ionCmsSection7"];
  // ionCmsSection8Data: IPageContent["ionCmsPageModel"]["ionCmsPage"]["ionCmsSection8"];
  ionCmsHoriTabsSectionData: IPageContent["ionCmsPageModel"]["ionCmsPage"]["ionCmsHoriTabsSection"];
  ionCmsSection9Data: IPageContent["ionCmsPageModel"]["ionCmsPage"]["ionCmsSection9"];
};

const CmsView: React.FC<IonCmsViewProps> = ({
  ionCmsSection1Data,
  ionCmsSection2Data,
  // ionCmsSection3Data,
  // ionCmsSection4Data,
  // ionCmsSection5Data,
  // ionCmsSection6Data,
  // ionCmsSection7Data,
  // ionCmsSection8Data,
  ionCmsHoriTabsSectionData,
  ionCmsSection9Data,
}) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [section8ActiveIndex, setSection8ActiveIndex] = useState(0);
  const [isSection1StackedLayout, setIsSection1StackedLayout] = useState(() => {
    if (typeof window === "undefined") {
      return false;
    }
    return window.matchMedia("(max-width: 820px)").matches;
  });
  const stickyRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const section2Ref = useRef<HTMLElement>(null);
  const section8StickyRef = useRef<HTMLDivElement>(null);
  const section8WrapperRef = useRef<HTMLElement>(null);

  // Section 2 : Once spaces are mapped...
  const { scrollYProgress: section2ScrollProgress } = useScroll({
    target: section2Ref,
    offset: ["start end", "end start"],
  });
  const section2BackgroundY = useTransform(
    section2ScrollProgress,
    [0, 1],
    [-100, 100]
  );

  // useEffect(() => {
  //   const mediaQuery = window.matchMedia("(max-width: 820px)");
  //   const updateLayout = () => setIsSection1StackedLayout(mediaQuery.matches);

  //   updateLayout();
  //   mediaQuery.addEventListener("change", updateLayout);

  //   return () => {
  //     mediaQuery.removeEventListener("change", updateLayout);
  //   };
  // }, []);

  // // Section 3 - 7 : Automated Floor Plan...
  // const stickyContent = [
  //   {
  //     data: ionCmsSection3Data,
  //     id: "section3",
  //     imagePosition: "left" as const,
  //   },
  //   {
  //     data: ionCmsSection4Data,
  //     id: "section4",
  //     imagePosition: "left" as const,
  //   },
  //   {
  //     data: ionCmsSection5Data,
  //     id: "section5",
  //     imagePosition: "left" as const,
  //   },
  //   {
  //     data: ionCmsSection6Data,
  //     id: "section6",
  //     imagePosition: "left" as const,
  //   },
  //   {
  //     data: ionCmsSection7Data,
  //     id: "section7",
  //     imagePosition: "left" as const,
  //   },
  // ];

  // useEffect(() => {
  //   const cleanup = initStickyScroll({
  //     wrapperRef,
  //     stickyRef,
  //     sections: stickyContent.length,
  //     setActiveIndex,
  //     scrollMultiplier: 2,
  //   });

  //   return () => {
  //     if (cleanup) {
  //       cleanup();
  //     }
  //   };
  // }, [stickyContent.length]);

  // useEffect(() => {
  //   const cleanup = initStickyScroll({
  //     wrapperRef: section8WrapperRef,
  //     stickyRef: section8StickyRef,
  //     sections: 3,
  //     setActiveIndex: setSection8ActiveIndex,
  //     scrollMultiplier: 1.25,
  //   });

  //   return () => {
  //     if (cleanup) {
  //       cleanup();
  //     }
  //   };
  // }, []);

  // Section 8 : Visibility Across People...
  // const section8Items = ionCmsSection8Data.gallery.slice(0, 3);
  const activeTab = ionCmsHoriTabsSectionData[activeIndex];
  const section1ImageDelay = isSection1StackedLayout ? 0 : 0.6;
  const section1TextBaseDelay = isSection1StackedLayout ? 0.55 : 0;


  return (
    <div id="IonCmsID">
      <div className={styles.gradientBkgd}></div>

      {/* ---------- Section 1 : The Command Center... ---------- */}
      <section className={styles.section1}>
        <div className={`${styles.maxWidthContainer} maxWidthContainer`}>
          <div className={styles.copyContainer}>
            <motion.span
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: section1TextBaseDelay, ease: "easeOut" }}
            >
              {parse(ionCmsSection1Data.overline)}
            </motion.span>
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: section1TextBaseDelay + 0.2, ease: "easeOut" }}
            >
              {parse(ionCmsSection1Data.heading)}
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: section1TextBaseDelay + 0.4, ease: "easeOut" }}
            >
              {parse(ionCmsSection1Data.body)}
            </motion.p>
          </div>

          <div className={styles.imgContainer}>
            <MotionImage
              src={ionCmsSection1Data.gallery[0].url}
              alt={ionCmsSection1Data.gallery[0].alt}
              width={500}
              height={500}
              priority
              fetchPriority="high"
              loading="eager"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: section1ImageDelay, ease: "easeOut" }}
            />
          </div>
        </div>
      </section>


      {/* ---------- Section 2 : Once spaces are mapped... ---------- */}
      <section ref={section2Ref} className={styles.section2}>
        <div className={`${styles.maxWidthContainer} maxWidthContainer`}>

          <div className={styles.copyContainer}>
            <motion.p
              className={styles.overline}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.5, ease: "easeOut" }}
            >
              {parse(ionCmsSection2Data.overline)}
            </motion.p>
            <motion.h2
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.5, delay: 0.2, ease: "easeOut" }}
            >
              {parse(ionCmsSection2Data.heading)}
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.5, delay: 0.4, ease: "easeOut" }}
            >
              {parse(ionCmsSection2Data.body)}
            </motion.p>
          </div>

          <motion.div
            className={styles.svgArrowWrap}
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.5, delay: 0.6, ease: "easeOut" }}
          >
            <MotionImage
              className={styles.svgArrowDraw}
              src={ionCmsSection2Data.gallery[0].url}
              alt={ionCmsSection2Data.gallery[0].alt}
              width={10}
              height={100}
              initial={{ opacity: 1, clipPath: "inset(0 0 100% 0)" }}
              animate={{
                opacity: 1,
                clipPath: [
                  "inset(0 0 100% 0)",
                  "inset(0 0 0% 0)",
                  "inset(100% 0 0% 0)",
                ],
              }}
              transition={{
                duration: 1.8,
                ease: "linear",
                repeat: Infinity,
                repeatDelay: 0.2,
                times: [0, 0.5, 1],
                repeatType: "loop",
              }}
            />
          </motion.div>
        </div>
      </section>


      {/* ---------- Section 3 - 7 : Automated Floor Plan Georeferencing ---------- */}
      {/* <section ref={wrapperRef} className={styles.stickyScrollWrapper}>
        <div ref={stickyRef} className={styles.stickyContent}>
          <div
            key={activeIndex}
            className={`${styles.maxWidthContainer} maxWidthContainer`}
          >
            <div className={styles.pagination}>
              {stickyContent.map((item, index) => (
                <div
                  key={item.id}
                  className={`${styles.paginationItem} ${
                    index <= activeIndex
                      ? styles.pastPaginationItem
                      : styles.upcomingPaginationItem
                  }`}
                />
              ))}
            </div>

            <div className={styles.copyContainer}>
              <h3>{parse(stickyContent[activeIndex].data.heading)}</h3>
              <p>{parse(stickyContent[activeIndex].data.body)}</p>
            </div>
            <div className={styles.imgContainer}>
              <Image
                key={`img-${activeIndex}`}
                src={stickyContent[activeIndex].data.gallery[0].url}
                alt={stickyContent[activeIndex].data.gallery[0].alt}
                fill
              />
            </div>

          </div>
        </div>
      </section> */}


      {/* ---------- Section 8 : Visibility Across People... ---------- */}
      {/* <section
        id="section8"
        ref={section8WrapperRef}
        className={`${styles.section8} section8`}
      >
        <div ref={section8StickyRef} className={styles.section8Sticky}>
          <div className={`${styles.maxWidthContainer} maxWidthContainer`}>
            <div className={styles.copyContainer}>
              <h3>{parse(ionCmsSection8Data.heading)}</h3>
              <p>{parse(ionCmsSection8Data.body)}</p>
            </div>

            <div className={styles.copyImgsContainer}>
              <div className={styles.leftCol}>
                <div className={styles.bulletsContainer}>
                  <Image
                    className={styles.svgLine}
                    src={ionCmsSection8Data.gallery[3].url}
                    alt={ionCmsSection8Data.gallery[3].alt}
                    fill
                  />
                  {section8Items.map((item, index) => {
                    const isActive = index === section8ActiveIndex;

                    return (
                      <div
                        key={item.url}
                        className={`${styles.bulletContainer} ${
                          isActive ? styles.activeBulletContainer : ""
                        }`}
                      >
                        <div className={styles.outerBullet}>
                          <div className={styles.innerBullet}></div>
                        </div>
                        <p>{item.alt}</p>
                      </div>
                    );
                  })}
                </div>
              </div>
              <div className={styles.rightCol}>
                {section8Items.map((item, index) => (
                  <Image
                    key={item.url}
                    className={`${styles.section8Image} ${
                      index === section8ActiveIndex ? styles.activeImage : ""
                    }`}
                    src={item.url}
                    alt={item.alt}
                    fill
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section> */}


      {/* ---------- Horizontal Tabs Section : Mapping Preparation & Validation ---------- */}
      <section id="horiTabsSection" className={`${styles.horiTabsSection} horiTabsSection`}>
        <div className={`${styles.maxWidthContainer} maxWidthContainer`}>

          <div className={`${styles.tabBtnsContainer}`}>
            <div className={`${styles.tabBtns}`}>
              {
                ionCmsHoriTabsSectionData.map((tabBtn, index) => (
                  <div 
                    key={index} 
                    className={`${styles.tabBtn} ${index === activeIndex ? styles.activeTabItem : ""}`} 
                    onClick={() => setActiveIndex(index)}
                  >
                    <ReactMarkdown
                      components={{ p: ({ children }) => <p>{children}</p> }}
                    >
                      {tabBtn.overline}
                    </ReactMarkdown>
                    {index === activeIndex && (
                      <motion.div
                        layoutId="activeBorder"
                        className={`${styles.activeBorder}`}
                        transition={{ type: "spring", stiffness: 500, damping: 38 }}
                      />
                    )}
                  </div>
                ))
              }
            </div>
            <div className={`${styles.tabsBar}`}></div>
          </div>

          <div className={styles.mobileTabBtnsContainer}>
            {ionCmsHoriTabsSectionData.map((tab, index) => {
              const isActive = index === activeIndex;

              return (
                <div
                  key={`mobile-tab-${index}`}
                  className={`${styles.mobileAccordionItem} ${isActive ? styles.activeTabItem : ""}`}
                >
                  <button
                    type="button"
                    className={styles.mobileTabBtn}
                    onClick={() => setActiveIndex(index)}
                    aria-expanded={isActive}
                  >
                    <ReactMarkdown
                      components={{ p: ({ children }) => <p>{children}</p> }}
                    >
                      {tab.overline}
                    </ReactMarkdown>
                  </button>

                  <AnimatePresence initial={false}>
                    {isActive && (
                      <motion.div
                        className={styles.mobileAccordionContent}
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3, ease: "easeOut" }}
                      >
                        <motion.div
                          className={styles.mobileAccordionCopy}
                          initial={{ opacity: 0, y: 12 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -8 }}
                          transition={{ duration: 0.25, ease: "easeOut" }}
                        >
                          <ReactMarkdown
                            components={{ p: ({ children }) => <h3>{children}</h3> }}
                          >
                            {tab.heading}
                          </ReactMarkdown>
                          <ReactMarkdown
                            components={{ p: ({ children }) => <p>{children}</p> }}
                          >
                            {tab.body}
                          </ReactMarkdown>
                        </motion.div>

                        <motion.div
                          className={styles.mobileAccordionImg}
                          initial={{ opacity: 0, y: 12 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -8 }}
                          transition={{ duration: 0.25, delay: 0.05, ease: "easeOut" }}
                        >
                          <Image
                            src={tab.gallery[0].url}
                            alt={tab.gallery[0].alt}
                            fill
                          />
                        </motion.div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>

          <div className={`${styles.tabItemImgsContainer}`}>
            <AnimatePresence mode="sync" initial={false}>
              {activeTab && (
                <motion.div
                  key={activeIndex}
                  className={`${styles.tabItem} ${styles.activeTabItem}`}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.3, ease: "easeOut" }}
                >
                  <motion.div
                    className={`${styles.tabItemCopy}`}
                    initial={{ opacity: 0, y: 25 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -5 }}
                    transition={{ duration: 0.55, ease: "easeOut" }}
                  >
                    <ReactMarkdown
                      components={{ p: ({ children }) => <h3>{children}</h3> }}
                    >
                      {activeTab.heading}
                    </ReactMarkdown>
                    <ReactMarkdown
                      components={{ p: ({ children }) => <p>{children}</p> }}
                    >
                      {activeTab.body}
                    </ReactMarkdown>
                  </motion.div>

                  <motion.div
                    className={`${styles.tabItemImg}`}
                    initial={{ opacity: 0, y: 25 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: 0.5, delay: 0.3, ease: "easeOut" }}
                  >
                    <Image
                      src={activeTab.gallery[0].url}
                      alt={activeTab.gallery[0].alt}
                      fill
                    />
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </section>


      {/* ---------- Section 9 : Enable partners to... ---------- */}
      <section id="section9" className={`${styles.section9} section9`}>
        <div className={`${styles.maxWidthContainer} maxWidthContainer`}>
          <div className={styles.copyContainer}>
            <h2>{parse(ionCmsSection9Data.heading)}</h2>
          </div>
          <div className={styles.btnsContainer}>
            <TransitionLink href="/contact-us">
              {parse(ionCmsSection9Data.button)}
            </TransitionLink>
            <TransitionLink href="/ecosystem/add-ons/tracking-device">
              {parse(ionCmsSection9Data.button2)}
            </TransitionLink>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CmsView;
